import * as React from 'react';
import React__default, { useState, useEffect, useRef, useContext, isValidElement } from 'react';
import styled, { ThemeProvider, css, ThemeContext } from 'styled-components';
import { formatInTimeZone } from 'date-fns-tz';
import enGB from 'date-fns/locale/en-GB';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip as Tooltip$1, Line } from 'recharts';
import { format } from 'date-fns';

var getFontSize = function getFontSize(baseFontSizeConverter) {
  return {
    xLarge: 1.125 * baseFontSizeConverter + 'rem',
    large: baseFontSizeConverter + 'rem',
    regular: 0.875 * baseFontSizeConverter + 'rem',
    small: 0.75 * baseFontSizeConverter + 'rem'
  };
};

var fontWeight = {
  bold: 600,
  semibold: 500,
  regular: 400
};
var fontFamily = {
  "default": '"Inter", sans-serif',
  secondary: '"Poppins", sans-serif',
  serif: '"Georgia"'
};

var getTheme = function getTheme(baseFontSizeConverter) {
  if (baseFontSizeConverter === void 0) {
    baseFontSizeConverter = 1;
  }

  return {
    baseFontSizeConverter: baseFontSizeConverter,
    typography: {
      heading: {
        fontFamily: fontFamily.secondary,
        fontWeight: fontWeight.bold
      },
      paragraph: {
        fontSize: {
          xLarge: {
            pc: getFontSize(baseFontSizeConverter).xLarge,
            mobile: getFontSize(baseFontSizeConverter).large
          },
          large: {
            pc: getFontSize(baseFontSizeConverter).large,
            mobile: getFontSize(baseFontSizeConverter).large
          },
          medium: {
            pc: getFontSize(baseFontSizeConverter).regular,
            mobile: getFontSize(baseFontSizeConverter).regular
          },
          small: {
            pc: getFontSize(baseFontSizeConverter).small,
            mobile: getFontSize(baseFontSizeConverter).small
          },
          subtitle: {
            pc: getFontSize(baseFontSizeConverter).xLarge,
            mobile: getFontSize(baseFontSizeConverter).xLarge
          },
          serif: {
            pc: getFontSize(baseFontSizeConverter).xLarge,
            mobile: getFontSize(baseFontSizeConverter).xLarge
          },
          quote: {
            pc: 2 * baseFontSizeConverter + 'rem',
            mobile: 1.25 * baseFontSizeConverter + 'rem'
          }
        },
        fontFamily: {
          serif: fontFamily.serif,
          quote: fontFamily.secondary,
          "default": fontFamily["default"]
        },
        fontWeight: {
          xLarge: fontWeight.bold,
          large: fontWeight.regular,
          medium: fontWeight.regular,
          small: fontWeight.regular,
          subtitle: fontWeight.semibold,
          serif: fontWeight.regular,
          quote: fontWeight.bold,
          "default": fontWeight.regular
        },
        lineHeight: {
          xLarge: '130%',
          large: '160%',
          medium: '160%',
          small: '130%',
          subtitle: '160%',
          serif: '160%',
          quote: '145%',
          "default": '130%'
        }
      },
      label: {
        fontSize: {
          category: getFontSize(baseFontSizeConverter).xLarge,
          date: getFontSize(baseFontSizeConverter).small,
          large: getFontSize(baseFontSizeConverter).large,
          "default": getFontSize(baseFontSizeConverter).regular
        },
        lineHeight: {
          category: 1.68 * baseFontSizeConverter + 'rem',
          "default": 1.06 * baseFontSizeConverter + 'rem'
        },
        fontWeight: {
          category: fontWeight.bold,
          regular: fontWeight.regular,
          "default": fontWeight.semibold
        },
        fontFamily: {
          category: fontFamily.secondary,
          "default": fontFamily["default"]
        }
      },
      link: {
        fontSize: {
          external: {
            pc: getFontSize(baseFontSizeConverter).xLarge,
            mobile: getFontSize(baseFontSizeConverter).large
          },
          internal: {
            pc: getFontSize(baseFontSizeConverter).large,
            mobile: getFontSize(baseFontSizeConverter).regular
          },
          internalUnderline: {
            pc: getFontSize(baseFontSizeConverter).small,
            mobile: getFontSize(baseFontSizeConverter).small
          },
          "default": getFontSize(baseFontSizeConverter).large
        },
        fontFamily: {
          external: fontFamily.serif,
          "default": fontFamily.secondary
        }
      },
      button: {
        fontSize: {
          heavy: {
            pc: getFontSize(baseFontSizeConverter).large,
            mobile: getFontSize(baseFontSizeConverter).large
          },
          light: {
            pc: getFontSize(baseFontSizeConverter).regular,
            mobile: getFontSize(baseFontSizeConverter).regular
          },
          "default": getFontSize(baseFontSizeConverter).large
        }
      }
    },
    colors: {
      title: '#000000',
      body: '#252525',
      label: '#808080',
      placeholder: '#A7A7A7',
      menu: '#E5E5E7',
      backgroundButton: '#F3F3F3',
      background: '#FFFFFF',
      success: '#23B427',
      warning: '#FF3737',
      darkBlue: '#032BA8'
    },
    fontFamily: fontFamily,
    fontSize: getFontSize(baseFontSizeConverter),
    fontWeight: fontWeight
  };
};

var theme = /*#__PURE__*/getTheme();

var CoinpaperThemeProvider = function CoinpaperThemeProvider(_ref) {
  var _ref$theme = _ref.theme,
      theme$1 = _ref$theme === void 0 ? theme : _ref$theme,
      children = _ref.children;
  return React__default.createElement(ThemeProvider, {
    theme: theme$1
  }, children);
};

function _extends$4() {
  _extends$4 = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends$4.apply(this, arguments);
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var convertFromBaseToRem = function convertFromBaseToRem(size) {
  return function (_ref) {
    var baseFontSizeConverter = _ref.theme.baseFontSizeConverter;
    return size * baseFontSizeConverter + 'rem';
  };
};

var formatPrice = function formatPrice(price, maximumFractionDigits) {
  if (maximumFractionDigits === void 0) {
    maximumFractionDigits = 10;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: maximumFractionDigits
  }).format(price);
};

var formatDateInTimeZone = function formatDateInTimeZone(date) {
  return formatInTimeZone(new Date(date), 'Europe/Berlin', 'LLL d HH:mm zzz', {
    locale: enGB
  });
};

var deviceSizes = {
  mobileS: 320,
  mobileM: 375,
  mobileL: 425,
  tablet: 768,
  laptop: 992,
  laptopL: 1200,
  desktop: 1440,
  desktopL: 1920
};
var MEDIA_QUERIES = {
  mobileS: "(min-width: " + deviceSizes.mobileS + "px)",
  mobileM: "(min-width: " + deviceSizes.mobileM + "px)",
  mobileL: "(min-width: " + deviceSizes.mobileL + "px)",
  tablet: "(min-width: " + deviceSizes.tablet + "px)",
  laptop: "(min-width: " + deviceSizes.laptop + "px)",
  laptopL: "(min-width: " + deviceSizes.laptopL + "px)",
  desktop: "(min-width: " + deviceSizes.desktop + "px)",
  desktopL: "(min-width: " + deviceSizes.desktopL + "px)"
};

var _templateObject$g, _templateObject2$7;
var H0 = /*#__PURE__*/styled.h1.withConfig({
  displayName: "headers__H0",
  componentId: "sc-188yf6o-0"
})(["font-family:", ";color:", ";font-weight:", ";font-size:", ";line-height:120%;"], function (_ref) {
  var theme = _ref.theme;
  return theme.typography.heading.fontFamily;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.colors.title;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.typography.heading.fontWeight;
}, /*#__PURE__*/convertFromBaseToRem(2.5));
var h1Style = /*#__PURE__*/css(_templateObject$g || (_templateObject$g = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-family: ", ";\n  color: ", ";\n  font-weight: ", ";\n  font-size: ", ";\n  line-height: 120%;\n\n  @media ", " {\n    font-size: ", ";\n  }\n"])), function (_ref4) {
  var theme = _ref4.theme;
  return theme.typography.heading.fontFamily;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.colors.title;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.typography.heading.fontWeight;
}, /*#__PURE__*/convertFromBaseToRem(1.75), MEDIA_QUERIES.tablet, /*#__PURE__*/convertFromBaseToRem(2));
var H1 = /*#__PURE__*/styled.h1.withConfig({
  displayName: "headers__H1",
  componentId: "sc-188yf6o-1"
})(["", ";"], h1Style);
var H2 = /*#__PURE__*/styled.h2.withConfig({
  displayName: "headers__H2",
  componentId: "sc-188yf6o-2"
})(["font-family:", ";color:", ";font-weight:", ";font-size:", ";line-height:120%;@media ", "{font-size:", ";}"], function (_ref7) {
  var theme = _ref7.theme;
  return theme.typography.heading.fontFamily;
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.colors.title;
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.typography.heading.fontWeight;
}, /*#__PURE__*/convertFromBaseToRem(1.125), MEDIA_QUERIES.tablet, /*#__PURE__*/convertFromBaseToRem(1.68));
var h3Style = /*#__PURE__*/css(_templateObject2$7 || (_templateObject2$7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-family: ", ";\n  color: ", ";\n  font-weight: ", ";\n  line-height: 120%;\n\n  @media ", " {\n    font-size: ", ";\n  }\n"])), function (_ref10) {
  var theme = _ref10.theme;
  return theme.typography.heading.fontFamily;
}, function (_ref11) {
  var theme = _ref11.theme;
  return theme.colors.title;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.typography.heading.fontWeight;
}, MEDIA_QUERIES.tablet, /*#__PURE__*/convertFromBaseToRem(1.5));
var H3 = /*#__PURE__*/styled.h3.withConfig({
  displayName: "headers__H3",
  componentId: "sc-188yf6o-3"
})(["", ";font-size:", ";"], h3Style, /*#__PURE__*/convertFromBaseToRem(1));
var H4 = /*#__PURE__*/styled.h4.withConfig({
  displayName: "headers__H4",
  componentId: "sc-188yf6o-4"
})(["font-family:", ";color:", ";font-weight:", ";font-size:", ";line-height:120%;"], function (_ref13) {
  var theme = _ref13.theme;
  return theme.typography.heading.fontFamily;
}, function (_ref14) {
  var theme = _ref14.theme;
  return theme.colors.title;
}, function (_ref15) {
  var theme = _ref15.theme;
  return theme.typography.heading.fontWeight;
}, /*#__PURE__*/convertFromBaseToRem(1.125));

var _templateObject$f;

var headingStyle = /*#__PURE__*/css(_templateObject$f || (_templateObject$f = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-family: ", ";\n  color: ", ";\n  font-weight: ", ";\n  ", ";\n  a {\n    font-family: inherit;\n    color: inherit;\n    font-weight: inherit;\n    font-size: inherit;\n    text-decoration-thickness: 1px;\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.typography.heading.fontFamily;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.colors.title;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.typography.heading.fontWeight;
}, {
  "marginTop": "0.5rem",
  "marginBottom": "0.5rem",
  "@media (min-width: 768px)": {
    "marginTop": "1.25rem",
    "marginBottom": "1.25rem"
  }
});

var Heading = /*#__PURE__*/styled.h2.withConfig({
  displayName: "Headingstyles__Heading",
  componentId: "sc-14lm248-0"
})(["", ";font-size:", ";", ";a{font-family:inherit;color:inherit;font-weight:inherit;font-size:inherit;text-decoration-thickness:1px;}"], h3Style, /*#__PURE__*/convertFromBaseToRem(1.5), {
  "marginTop": "0.5rem",
  "marginBottom": "0.5rem",
  "@media (min-width: 768px)": {
    "marginTop": "1.25rem",
    "marginBottom": "1.25rem"
  }
});
var Heading3 = /*#__PURE__*/styled.h3.withConfig({
  displayName: "Headingstyles__Heading3",
  componentId: "sc-14lm248-1"
})(["", ";font-size:", ";line-height:120%;"], headingStyle, /*#__PURE__*/convertFromBaseToRem(1.4));
var Heading4 = /*#__PURE__*/styled.h4.withConfig({
  displayName: "Headingstyles__Heading4",
  componentId: "sc-14lm248-2"
})(["", ";font-size:", ";line-height:120%;"], headingStyle, /*#__PURE__*/convertFromBaseToRem(1.3));
var Heading5 = /*#__PURE__*/styled.h5.withConfig({
  displayName: "Headingstyles__Heading5",
  componentId: "sc-14lm248-3"
})(["", ";font-size:", ";line-height:110%;"], headingStyle, /*#__PURE__*/convertFromBaseToRem(1.2));
var Heading6 = /*#__PURE__*/styled.h6.withConfig({
  displayName: "Headingstyles__Heading6",
  componentId: "sc-14lm248-4"
})(["", ";font-size:", ";line-height:110%;"], headingStyle, /*#__PURE__*/convertFromBaseToRem(1.1));

var Figure = /*#__PURE__*/styled.figure.withConfig({
  displayName: "Quotestyles__Figure",
  componentId: "sc-176zamo-0"
})(["", ";", ";border-left:", ";padding-left:20px;"], {
  "gridColumn": "span 12 / span 12",
  "gridColumnStart": "1",
  "@media (min-width: 768px)": {
    "gridColumn": "span 10 / span 10",
    "gridColumnStart": "1"
  }
}, {
  "marginTop": "0.5rem",
  "marginBottom": "0.5rem",
  "paddingTop": "2rem",
  "paddingBottom": "2rem",
  "@media (min-width: 768px)": {
    "marginTop": "1.25rem",
    "marginBottom": "1.25rem"
  }
}, function (_ref) {
  var theme = _ref.theme;
  return "1px dashed " + theme.colors.title;
});
var Blockquote = /*#__PURE__*/styled.blockquote.withConfig({
  displayName: "Quotestyles__Blockquote",
  componentId: "sc-176zamo-1"
})(["", ";p{", ";font-style:italic;font-weight:500;font-size:", ";line-height:", ";}"], {
  "margin": "0px"
}, {
  "fontFamily": "\"Poppins\", sans-serif",
  "margin": "0px"
}, /*#__PURE__*/convertFromBaseToRem(1.125), /*#__PURE__*/convertFromBaseToRem(1.75));
var Figcaption = /*#__PURE__*/styled.figcaption.withConfig({
  displayName: "Quotestyles__Figcaption",
  componentId: "sc-176zamo-2"
})(["", ";p{", ";font-style:normal;font-weight:normal;font-size:", ";line-height:", ";color:", ";}"], {
  "margin": "0px",
  "paddingTop": "2rem"
}, {
  "fontFamily": "\"Inter\", sans-serif"
}, /*#__PURE__*/convertFromBaseToRem(1.125), /*#__PURE__*/convertFromBaseToRem(1.438), function (_ref2) {
  var theme = _ref2.theme;
  return theme.colors.label;
});

var TableContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TableBasestyles__TableContainer",
  componentId: "sc-ni2zca-0"
})(["", ";", ";max-width:100%;display:flex;justify-content:center;overflow:visible;"], {
  "gridColumn": "span 12 / span 12",
  "gridColumnStart": "1",
  "@media (min-width: 768px)": {
    "gridColumn": "span 10 / span 10",
    "gridColumnStart": "1"
  }
}, {
  "marginTop": "0.5rem",
  "marginBottom": "0.5rem",
  "@media (min-width: 768px)": {
    "marginTop": "1.25rem",
    "marginBottom": "1.25rem"
  }
});
var Table = /*#__PURE__*/styled.table.withConfig({
  displayName: "TableBasestyles__Table",
  componentId: "sc-ni2zca-1"
})(["", ";border:", ";border-collapse:collapse;margin:0 auto;"], {
  "tableLayout": "fixed"
}, function (_ref) {
  var theme = _ref.theme;
  return "1px solid " + theme.colors.title;
});
var Tbody = /*#__PURE__*/styled.tbody.withConfig({
  displayName: "TableBasestyles__Tbody",
  componentId: "sc-ni2zca-2"
})(["display:block;max-width:98vw;overflow-x:auto;"]);

var TableBase = function TableBase(_ref) {
  var children = _ref.children;
  return React__default.createElement(TableContainer, null, React__default.createElement(Table, null, React__default.createElement(Tbody, null, children)));
};

var Td = /*#__PURE__*/styled.td.withConfig({
  displayName: "TableCellstyles__Td",
  componentId: "sc-1bi2mgr-0"
})(["height:64px;width:170px;padding:3px 12px;border-right:", ";border-bottom:", ";@media ", "{height:104px;width:250px;}"], function (_ref) {
  var theme = _ref.theme;
  return "1px dashed " + theme.colors.title;
}, function (_ref2) {
  var theme = _ref2.theme;
  return "1px dashed " + theme.colors.title;
}, MEDIA_QUERIES.tablet);

var TableCell = function TableCell(_ref) {
  var children = _ref.children;
  return React__default.createElement(Td, null, children);
};

var _templateObject$e;
var getButtonStyle = function getButtonStyle(variant) {
  if (variant === void 0) {
    variant = 'default';
  }

  return css(_templateObject$e || (_templateObject$e = _taggedTemplateLiteralLoose(["\n  font-family: 'Inter', sans-serif;\n  color: ", ";\n  font-weight: 600;\n  font-size: ", ";\n  line-height: ", ";\n  text-transform: ", ";\n  letter-spacing: ", ";\n  @media ", " {\n    font-size: ", ";\n  }\n"])), function (_ref) {
    var theme = _ref.theme;
    return theme.colors.title;
  }, function (_ref2) {
    var _theme$typography$but, _theme$typography$but2;

    var theme = _ref2.theme;
    return (_theme$typography$but = (_theme$typography$but2 = theme.typography.button.fontSize[variant]) == null ? void 0 : _theme$typography$but2.mobile) != null ? _theme$typography$but : theme.typography.button.fontSize["default"];
  }, convertFromBaseToRem(variant === 'heavy' ? 1.187 : 1.312), variant === 'heavy' && 'uppercase', variant === 'heavy' && '1px', MEDIA_QUERIES.tablet, function (_ref3) {
    var _theme$typography$but3, _theme$typography$but4;

    var theme = _ref3.theme;
    return (_theme$typography$but3 = (_theme$typography$but4 = theme.typography.button.fontSize[variant]) == null ? void 0 : _theme$typography$but4.pc) != null ? _theme$typography$but3 : theme.typography.button.fontSize["default"];
  });
};
var Button$1 = /*#__PURE__*/styled.button.withConfig({
  displayName: "buttons__Button",
  componentId: "sc-fnahwg-0"
})(["", ";"], function (_ref4) {
  var variant = _ref4.variant;
  return getButtonStyle(variant);
});

var Th = /*#__PURE__*/styled.th.withConfig({
  displayName: "TableHeaderstyles__Th",
  componentId: "sc-1esrbg3-0"
})(["", ";text-align:center;border:", ";padding:7px 32px;@media ", "{width:160px;}"], /*#__PURE__*/getButtonStyle('heavy'), function (_ref) {
  var theme = _ref.theme;
  return "1px solid " + theme.colors.title;
}, MEDIA_QUERIES.tablet);

var TableHeader = function TableHeader(_ref) {
  var children = _ref.children,
      scope = _ref.scope;
  return React__default.createElement(Th, {
    scope: scope
  }, children);
};

var TableRow = function TableRow(row) {
  return React__default.createElement("tr", null, row.children.map(function (props, index) {
    if (props.type === 'tableHeader') {
      return React__default.createElement(TableHeader, _extends$4({
        key: index
      }, props));
    }

    return React__default.createElement(TableCell, _extends$4({
      key: index
    }, props));
  }));
};

var _templateObject$d;
var getParagraphStyle = function getParagraphStyle(variant) {
  if (variant === void 0) {
    variant = 'default';
  }

  return css(_templateObject$d || (_templateObject$d = _taggedTemplateLiteralLoose(["\n  color: ", ";\n  font-family: ", ";\n  font-size: ", ";\n  line-height: ", ";\n  font-weight: ", ";\n  font-style: ", ";\n  @media ", " {\n    font-size: ", ";\n  }\n"])), function (_ref) {
    var theme = _ref.theme;
    return theme.colors.title;
  }, function (_ref2) {
    var theme = _ref2.theme;
    return theme.typography.paragraph.fontFamily[variant];
  }, function (_ref3) {
    var _theme$typography$par, _theme$typography$par2;

    var theme = _ref3.theme;
    return (_theme$typography$par = (_theme$typography$par2 = theme.typography.paragraph.fontSize[variant]) == null ? void 0 : _theme$typography$par2.mobile) != null ? _theme$typography$par : theme.typography.paragraph.fontSize["default"];
  }, function (_ref4) {
    var theme = _ref4.theme;
    return theme.typography.paragraph.lineHeight[variant];
  }, function (_ref5) {
    var theme = _ref5.theme;
    return theme.typography.paragraph.fontWeight[variant];
  }, variant === 'quote' && 'italic', MEDIA_QUERIES.tablet, function (_ref6) {
    var _theme$typography$par3, _theme$typography$par4;

    var theme = _ref6.theme;
    return (_theme$typography$par3 = (_theme$typography$par4 = theme.typography.paragraph.fontSize[variant]) == null ? void 0 : _theme$typography$par4.pc) != null ? _theme$typography$par3 : theme.typography.paragraph.fontSize["default"];
  });
};

var P = /*#__PURE__*/styled.p.withConfig({
  displayName: "paragraphs__P",
  componentId: "sc-z84r63-0"
})(["", ";"], function (_ref7) {
  var variant = _ref7.variant;
  return getParagraphStyle(variant);
});

var Label = /*#__PURE__*/styled.label.withConfig({
  displayName: "labels__Label",
  componentId: "sc-h2og64-0"
})(["color:", ";font-family:", ";font-weight:", ";font-size:", ";line-height:", ";letter-spacing:", ";text-transform:", ";text-decoration:", ";"], function (_ref) {
  var theme = _ref.theme;
  return theme.colors.title;
}, function (_ref2) {
  var theme = _ref2.theme,
      _ref2$variant = _ref2.variant,
      variant = _ref2$variant === void 0 ? 'default' : _ref2$variant;
  return theme.typography.label.fontFamily[variant];
}, function (_ref3) {
  var theme = _ref3.theme,
      _ref3$variant = _ref3.variant,
      variant = _ref3$variant === void 0 ? 'default' : _ref3$variant;
  return theme.typography.label.fontWeight[variant];
}, function (_ref4) {
  var theme = _ref4.theme,
      _ref4$variant = _ref4.variant,
      variant = _ref4$variant === void 0 ? 'default' : _ref4$variant;
  return theme.typography.label.fontSize[variant];
}, function (_ref5) {
  var theme = _ref5.theme,
      _ref5$variant = _ref5.variant,
      variant = _ref5$variant === void 0 ? 'default' : _ref5$variant;
  return theme.typography.label.lineHeight[variant];
}, function (_ref6) {
  var variant = _ref6.variant,
      baseFontSizeConverter = _ref6.theme.baseFontSizeConverter;
  return variant === 'category' && 0.1 * baseFontSizeConverter + 'rem';
}, function (_ref7) {
  var variant = _ref7.variant;
  return variant === 'category' && 'uppercase';
}, function (_ref8) {
  var variant = _ref8.variant;
  return variant === 'underline' && 'underline';
});

var A = /*#__PURE__*/styled.a.withConfig({
  displayName: "links__A",
  componentId: "sc-1xj1poj-0"
})(["color:", ";font-weight:", ";font-family:", ";font-size:", ";text-decoration:", ";line-height:1.8rem;transition:all 0.3s ease-in-out;display:inline-flex;align-items:center;filter:grayscale(100%);svg{transition:all 0.3s ease-in-out;}&:hover{color:", ";filter:grayscale(0);}@media ", "{font-size:", ";}"], function (_ref) {
  var theme = _ref.theme;
  return theme.colors.title;
}, function (_ref2) {
  var theme = _ref2.theme,
      variant = _ref2.variant;
  return variant === 'external' ? theme.typography.paragraph.fontWeight.medium : theme.typography.paragraph.fontWeight.xLarge;
}, function (_ref3) {
  var theme = _ref3.theme,
      _ref3$variant = _ref3.variant,
      variant = _ref3$variant === void 0 ? 'default' : _ref3$variant;
  return theme.typography.link.fontFamily[variant];
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.typography.link.fontSize["default"];
}, function (_ref5) {
  var variant = _ref5.variant;
  return variant === 'external' || variant === 'internalUnderline' ? 'underline' : 'none';
}, function (_ref6) {
  var theme = _ref6.theme,
      variant = _ref6.variant,
      hoverColor = _ref6.hoverColor;
  return variant === 'asset' && hoverColor ? hoverColor : theme.colors.label;
}, MEDIA_QUERIES.tablet, function (_ref7) {
  var _theme$typography$lin;

  var theme = _ref7.theme,
      _ref7$variant = _ref7.variant,
      variant = _ref7$variant === void 0 ? 'default' : _ref7$variant;
  return (_theme$typography$lin = theme.typography.link.fontSize[variant]) == null ? void 0 : _theme$typography$lin.pc;
});

var useScript = function useScript(src) {
  var _useState = useState(src ? 'loading' : 'idle'),
      status = _useState[0],
      setStatus = _useState[1];

  useEffect(function () {
    if (!src) {
      setStatus('idle');
      return undefined;
    }

    var script = document.querySelector("script[src=\"" + src + "\"]");

    if (!script) {
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      document.body.appendChild(script);

      var setAttributeFromEvent = function setAttributeFromEvent(event) {
        var _script;

        (_script = script) == null ? void 0 : _script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
      };

      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      setStatus(script.getAttribute('data-status'));
    }

    var setStateFromEvent = function setStateFromEvent(event) {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };

    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);
    return function () {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [src]);
  return status;
};

var useTweet = function useTweet(id, el, options) {
  var script = useScript('https://platform.twitter.com/widgets.js');

  var _useState = useState('loading'),
      status = _useState[0],
      setStatus = _useState[1];

  var target = el;
  useEffect(function () {
    setStatus('loading');

    if (script === 'ready' && target) {
      target.innerHTML = '';
      window.twttr.widgets.createTweet(id, target, options).then(function (element) {
        if (element) setStatus('ready');else setStatus('error');
      });
    }
  }, [id, target, options, script]);
  return status;
};

var _templateObject$c;
var Container$4 = /*#__PURE__*/styled.div(_templateObject$c || (_templateObject$c = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: relative;\n  min-height: 200px;\n  max-width: 550px;\n  width: 100%;\n"])));

var _path$2;

function _extends$3() { _extends$3 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var SvgLoader = function SvgLoader(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    width: 25,
    height: 24,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    d: "M2 12a10 10 0 1 1 2.547 6.667",
    stroke: "#000",
    strokeWidth: 2,
    strokeLinecap: "square",
    strokeLinejoin: "round"
  })));
};

var _templateObject$b, _templateObject2$6;
var Container$3 = /*#__PURE__*/styled.div(_templateObject$b || (_templateObject$b = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  max-width: 550px;\n  width: 100%;\n  height: 200px;\n  border: 1px solid rgb(207, 217, 222);\n  border-radius: 12px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"])));
var Icon = /*#__PURE__*/styled(SvgLoader)(_templateObject2$6 || (_templateObject2$6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  animation: 1.5s spin infinite linear;\n  path {\n    stroke: #1d9bf0;\n  }\n  @keyframes spin {\n    from {\n      transform: rotate(0deg);\n    }\n    to {\n      transform: rotate(360deg);\n    }\n  }\n"])));

var TweetLoading = function TweetLoading() {
  return React__default.createElement(Container$3, null, React__default.createElement(Icon, null));
};

var Tweet = function Tweet(_ref) {
  var id = _ref.id,
      options = _ref.options;
  var containerRef = useRef(null);

  var _useState = useState(true),
      loading = _useState[0],
      setLoading = _useState[1];

  var tweetStatus = useTweet(id, containerRef.current, options);
  useEffect(function () {
    setLoading(tweetStatus === 'loading');
  }, [id, tweetStatus]);
  return React__default.createElement(Container$4, null, React__default.createElement("div", {
    ref: containerRef
  }), loading && React__default.createElement(TweetLoading, null));
};

function _extends$2() { _extends$2 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var SvgArrowRight = function SvgArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    width: 14,
    height: 14,
    viewBox: "0 0 11 11",
    xmlns: "http://www.w3.org/2000/svg",
    xmlSpace: "preserve",
    style: {
      fillRule: "evenodd",
      clipRule: "evenodd",
      strokeLinejoin: "round",
      strokeMiterlimit: 2
    }
  }, props), /*#__PURE__*/React.createElement("path", {
    d: "M3.998 11.139c2.178 0 3.764-1.248 3.759-2.968.005-1.273-.791-2.187-2.217-2.391V5.7c1.103-.238 1.844-1.059 1.839-2.202C7.384 1.947 6.062.679 4.028.679 2.05.679.518 1.857.479 3.563h1.774c.03-.856.826-1.388 1.765-1.388.95 0 1.581.577 1.576 1.432.005.89-.731 1.482-1.785 1.482H2.91v1.422h.899c1.288 0 2.054.646 2.049 1.566.005.9-.776 1.516-1.865 1.516-1.024 0-1.814-.532-1.859-1.362H.265c.049 1.72 1.586 2.908 3.733 2.908ZM9.483 11h1.8V6.357c0-.94.626-1.576 1.402-1.576.761 0 1.283.512 1.283 1.297V11h1.765V6.237c0-.86.512-1.456 1.382-1.456.726 0 1.302.427 1.302 1.372V11h1.805V5.874c0-1.705-.984-2.61-2.386-2.61-1.109 0-1.954.547-2.292 1.397h-.08c-.293-.865-1.034-1.397-2.063-1.397-1.024 0-1.79.527-2.108 1.397h-.089V3.364H9.483V11Zm12.565 0h1.8V3.364h-1.8V11Zm.905-8.72c.571 0 1.039-.438 1.039-.975 0-.542-.468-.979-1.039-.979-.577 0-1.044.437-1.044.979 0 .537.467.975 1.044.975Zm4.545 4.246c0-1.104.666-1.74 1.616-1.74.929 0 1.486.611 1.486 1.63V11h1.8V6.138c.005-1.83-1.039-2.874-2.615-2.874-1.144 0-1.929.547-2.277 1.397h-.09V3.364h-1.72V11h1.8V6.526ZM37.702 11h1.8V6.511c0-.97.731-1.656 1.72-1.656.303 0 .681.055.835.105V3.304a4.291 4.291 0 0 0-.646-.05c-.875 0-1.606.497-1.884 1.382h-.08V3.364h-1.745V11Zm8.617.149c1.78 0 3.003-.87 3.321-2.197l-1.68-.189c-.244.646-.84.984-1.616.984-1.163 0-1.934-.765-1.949-2.073h5.32v-.552c0-2.68-1.611-3.858-3.49-3.858-2.188 0-3.615 1.606-3.615 3.963 0 2.396 1.407 3.922 3.709 3.922ZM44.4 6.461c.055-.974.776-1.795 1.85-1.795 1.034 0 1.73.756 1.74 1.795H44.4Zm9.035 4.693c1.198 0 1.914-.562 2.242-1.203h.06V11h1.73V5.889c0-2.018-1.646-2.625-3.103-2.625-1.605 0-2.838.716-3.236 2.108l1.68.239c.179-.522.686-.97 1.566-.97.836 0 1.293.428 1.293 1.179v.029c0 .517-.542.542-1.889.687-1.482.159-2.899.601-2.899 2.321 0 1.502 1.099 2.297 2.556 2.297Zm.467-1.322c-.751 0-1.288-.343-1.288-1.005 0-.691.602-.979 1.407-1.093.473-.065 1.417-.184 1.651-.373v.9c0 .85-.686 1.571-1.77 1.571Zm8.208 1.302c1.313 0 1.904-.78 2.188-1.337h.109V11h1.77V.818h-1.805v3.808h-.074c-.274-.551-.836-1.362-2.183-1.362-1.765 0-3.152 1.382-3.152 3.928 0 2.515 1.347 3.942 3.147 3.942Zm.502-1.476c-1.188 0-1.814-1.044-1.814-2.476 0-1.422.616-2.441 1.814-2.441 1.159 0 1.795.959 1.795 2.441 0 1.481-.646 2.476-1.795 2.476Zm15.19 1.71 4.822-4.823-4.822-4.822-1.024 1.019 3.067 3.063h-7.452v1.481h7.452l-3.067 3.068 1.024 1.014Z",
    style: {
      fillRule: "nonzero"
    },
    transform: "translate(-72 -1)"
  }));
};

var Arrow = /*#__PURE__*/styled(SvgArrowRight).withConfig({
  displayName: "Linkstyles__Arrow",
  componentId: "sc-kjxgm6-0"
})(["display:inline;margin-left:0.5rem;"]);
var LinkWrapper = /*#__PURE__*/styled(A).withConfig({
  displayName: "Linkstyles__LinkWrapper",
  componentId: "sc-kjxgm6-1"
})(["display:", ";&:hover ", "{fill:", ";}"], function (_ref) {
  var variant = _ref.variant;
  return variant !== 'asset' && 'unset';
}, Arrow, function (_ref2) {
  var hoverColor = _ref2.hoverColor,
      theme = _ref2.theme;
  return hoverColor != null ? hoverColor : theme.colors.label;
});

var Link$2 = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var href = _ref.href,
      _ref$target = _ref.target,
      target = _ref$target === void 0 ? '_self' : _ref$target,
      children = _ref.children,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'default' : _ref$variant,
      prepend = _ref.prepend,
      hoverColor = _ref.hoverColor,
      _ref$showArrow = _ref.showArrow,
      showArrow = _ref$showArrow === void 0 ? false : _ref$showArrow,
      className = _ref.className,
      rel = _ref.rel,
      onClick = _ref.onClick;
  return React__default.createElement(LinkWrapper, {
    ref: ref,
    onClick: onClick,
    href: href,
    target: target,
    variant: variant,
    hoverColor: hoverColor,
    className: className,
    rel: rel
  }, prepend, React__default.createElement("span", null, children), showArrow && React__default.createElement(Arrow, null));
});

var useMediaQuery = function useMediaQuery(query) {
  var _useState = useState(false),
      matches = _useState[0],
      setMatches = _useState[1];

  useEffect(function () {
    var media = window.matchMedia(query);

    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    var listener = function listener() {
      setMatches(media.matches);
    };

    try {
      media.addEventListener('change', listener);
    } catch (e) {
      // Fallback for Safari < 14 and older browsers
      media.addListener(listener);
    }

    return function () {
      try {
        media.removeEventListener('change', listener);
      } catch (e) {
        // Fallback for Safari < 14 and older browsers
        media.removeListener(listener);
      }
    };
  }, [matches, query]);
  return matches;
};

var useTabs = function useTabs(activeTab) {
  if (activeTab === void 0) {
    activeTab = '';
  }

  var _useState = useState(activeTab),
      active = _useState[0],
      setActive = _useState[1];

  return [active, setActive];
};

var _templateObject$a;
var LogoImage = /*#__PURE__*/styled.img(_templateObject$a || (_templateObject$a = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  margin: 0;\n  padding: 0;\n  margin-right: 8px;\n  width: 32px;\n  height: 32px;\n\n  @media ", " {\n    width: 48px;\n    height: 48px;\n  }\n"])), MEDIA_QUERIES.tablet);

var Logo = function Logo(_ref) {
  var logoSrc = _ref.logoSrc;

  if (!logoSrc) {
    return null;
  }

  return React__default.createElement(LogoImage, {
    src: logoSrc,
    alt: "asset logo"
  });
};

var _templateObject$9, _templateObject2$5, _templateObject3$2, _templateObject4$2, _templateObject5$2, _templateObject6$1, _templateObject7$1;
var HeaderWrapper = /*#__PURE__*/styled.div(_templateObject$9 || (_templateObject$9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  padding: 12px 16px;\n  display: flex;\n  justify-content: space-between;\n"])));
var Title$1 = /*#__PURE__*/styled.div(_templateObject2$5 || (_templateObject2$5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n"])));
var Price$1 = /*#__PURE__*/styled.div(_templateObject3$2 || (_templateObject3$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  flex-direction: column;\n  align-items: flex-end;\n  justify-content: space-between;\n"])));
var ChartH3 = /*#__PURE__*/styled(H3)(_templateObject4$2 || (_templateObject4$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-size: 1rem;\n  @media ", " {\n    font-size: 1.5rem;\n  }\n"])), MEDIA_QUERIES.tablet);
var ChartLabel = /*#__PURE__*/styled(Label)(_templateObject5$2 || (_templateObject5$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: ", ";\n  text-transform: uppercase;\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.colors.placeholder;
});
var Change = /*#__PURE__*/styled.span(_templateObject6$1 || (_templateObject6$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: ", ";\n"])), function (_ref2) {
  var variant = _ref2.variant,
      theme = _ref2.theme;
  return variant === 'positive' ? theme.colors.success : theme.colors.warning;
});
var ChartType = /*#__PURE__*/styled.span(_templateObject7$1 || (_templateObject7$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  color: ", ";\n  display: none;\n  @media ", " {\n    display: inline;\n    &:before {\n      margin: 0 8px;\n      content: '\u2022';\n      height: 10px;\n    }\n  }\n"])), function (_ref3) {
  var theme = _ref3.theme;
  return theme.colors.label;
}, MEDIA_QUERIES.tablet);

var ChartHeader = function ChartHeader(_ref) {
  var name = _ref.name,
      shortname = _ref.shortname,
      price = _ref.price,
      change = _ref.change,
      _ref$logoSrc = _ref.logoSrc,
      logoSrc = _ref$logoSrc === void 0 ? '' : _ref$logoSrc,
      option = _ref.option;
  var visibleChange = Boolean(change);
  var changeVariant = change > 0 ? 'positive' : 'negative';
  var formattedPrice = formatPrice(price);
  return React__default.createElement(HeaderWrapper, null, React__default.createElement(Title$1, null, React__default.createElement(Logo, {
    logoSrc: logoSrc
  }), React__default.createElement("div", null, React__default.createElement(ChartH3, null, name), React__default.createElement(ChartLabel, null, shortname))), React__default.createElement(Price$1, null, React__default.createElement(ChartH3, null, formattedPrice), React__default.createElement(Label, null, visibleChange && React__default.createElement(Change, {
    variant: changeVariant
  }, change.toFixed(2), "%"), React__default.createElement(ChartType, null, option))));
};

var _templateObject$8;
var Link$1 = /*#__PURE__*/styled.a(_templateObject$8 || (_templateObject$8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: inline-flex;\n  justify-content: center;\n  align-items: center;\n  width: 100%;\n  height: 39px;\n  border-top: 2px solid ", ";\n  transition: 0.3s ease-in-out;\n  cursor: pointer;\n  text-transform: uppercase;\n  color: ", ";\n  font-weight: ", ";\n  font-size: ", ";\n  letter-spacing: 1px;\n  line-height: 1.3rem;\n  &:hover {\n    color: ", ";\n    background: ", ";\n  }\n  @media ", " {\n    display: none;\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.colors.title;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.colors.title;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.fontWeight.bold;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.fontSize.regular;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.colors.background;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.colors.title;
}, MEDIA_QUERIES.laptop);

var ChartFooter = function ChartFooter(_ref) {
  var _ref$href = _ref.href,
      href = _ref$href === void 0 ? '' : _ref$href,
      displaySection = _ref.displaySection;

  if (!displaySection) {
    return React__default.createElement(React__default.Fragment, null);
  }

  return React__default.createElement(Link$1, {
    href: href
  }, "Open more stats");
};

var _templateObject$7;
var AssetInfoWrapper = /*#__PURE__*/styled.ul(_templateObject$7 || (_templateObject$7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  width: 100%;\n  display: none;\n  grid-template-columns: repeat(5, auto);\n  grid-gap: 64px;\n  list-style-type: none;\n  margin: 0;\n  padding: ", ";\n  border-bottom: ", ";\n  @media ", " {\n    display: grid;\n  }\n"])), function (_ref) {
  var variant = _ref.variant;
  return variant === 'asset' ? '12px 16px 16px' : '0 16px';
}, function (_ref2) {
  var variant = _ref2.variant,
      theme = _ref2.theme;
  return variant === 'asset' && "2px solid " + theme.colors.title;
}, MEDIA_QUERIES.laptop);

var _templateObject$6, _templateObject2$4;
var Title = /*#__PURE__*/styled.h3(_templateObject$6 || (_templateObject$6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-size: ", ";\n  font-weight: ", ";\n  color: ", ";\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.fontSize.regular;
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.fontWeight.semibold;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.colors.label;
});
var Value = /*#__PURE__*/styled.span(_templateObject2$4 || (_templateObject2$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-size: ", ";\n  font-weight: ", ";\n  color: ", ";\n"])), function (_ref4) {
  var theme = _ref4.theme;
  return theme.fontSize.regular;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.fontWeight.bold;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.colors.title;
});

var AssetInfoItem = function AssetInfoItem(_ref) {
  var title = _ref.title,
      value = _ref.value;
  return React__default.createElement("li", null, React__default.createElement(Title, null, title), React__default.createElement(Value, null, value));
};

var AssetInfo = function AssetInfo(_ref) {
  var info = _ref.info,
      variant = _ref.variant,
      _ref$visible = _ref.visible,
      visible = _ref$visible === void 0 ? true : _ref$visible;

  if (!visible) {
    return null;
  }

  return React__default.createElement(AssetInfoWrapper, {
    variant: variant
  }, info.map(function (_ref2) {
    var title = _ref2.title,
        value = _ref2.value;
    return React__default.createElement(AssetInfoItem, {
      title: title,
      value: value,
      key: title
    });
  }));
};

var _templateObject$5, _templateObject2$3, _templateObject3$1, _templateObject4$1, _templateObject5$1;
var ParagraphStyle = /*#__PURE__*/css(_templateObject$5 || (_templateObject$5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: absolute;\n  color: ", ";\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.colors.label;
});
var Container$2 = /*#__PURE__*/styled.div(_templateObject2$3 || (_templateObject2$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  position: relative;\n  margin-top: 8px;\n"])));
var Text = /*#__PURE__*/styled(P)(_templateObject3$1 || (_templateObject3$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n  left: 0;\n"])), ParagraphStyle);
var DateText = /*#__PURE__*/styled(P)(_templateObject4$1 || (_templateObject4$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  ", "\n  right: 0;\n"])), ParagraphStyle);
var Link = /*#__PURE__*/styled.a(_templateObject5$1 || (_templateObject5$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  text-decoration: underline;\n"])));

var ChartCaption = function ChartCaption(_ref) {
  var link = _ref.link,
      date = _ref.date;
  var hasLink = Boolean(link == null ? void 0 : link.name);
  return React__default.createElement(Container$2, null, hasLink && React__default.createElement(Text, {
    variant: "medium"
  }, "Powered by ", React__default.createElement(Link, {
    href: link == null ? void 0 : link.href
  }, link == null ? void 0 : link.name)), React__default.createElement(DateText, {
    variant: "medium"
  }, date));
};

var _templateObject$4, _templateObject2$2;
var ChartWrapper = /*#__PURE__*/styled.div(_templateObject$4 || (_templateObject$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border: 2px solid ", ";\n  width: 100%;\n  @media ", " {\n    border-bottom: ", ";\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return theme.colors.title;
}, MEDIA_QUERIES.laptop, function (_ref2) {
  var variant = _ref2.variant;
  return variant === 'asset' && 'none';
});
var ChartBodyContainer = /*#__PURE__*/styled.div(_templateObject2$2 || (_templateObject2$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  width: 100%;\n  height: 240px;\n"])));

var _templateObject$3, _templateObject2$1;
var LoadingWrapper = /*#__PURE__*/styled.div(_templateObject$3 || (_templateObject$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  width: 100%;\n  height: 240px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"])));
var Loading = /*#__PURE__*/styled(SvgLoader)(_templateObject2$1 || (_templateObject2$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  animation: 1.5s spin infinite linear;\n  @keyframes spin {\n    from {\n      transform: rotate(0deg);\n    }\n    to {\n      transform: rotate(360deg);\n    }\n  }\n"])));

var ChartLoading = function ChartLoading() {
  return React__default.createElement(LoadingWrapper, null, React__default.createElement(Loading, null));
};

var CustomDot = function CustomDot(_ref) {
  var _ref$cx = _ref.cx,
      cx = _ref$cx === void 0 ? 0 : _ref$cx,
      _ref$cy = _ref.cy,
      cy = _ref$cy === void 0 ? 0 : _ref$cy;
  return React__default.createElement("svg", {
    width: "20",
    height: "20",
    x: cx - 10,
    y: cy - 10,
    viewBox: "0 0 20 20",
    fill: "none"
  }, React__default.createElement("circle", {
    cx: "10",
    cy: "10",
    r: "6",
    fill: "black"
  }), React__default.createElement("circle", {
    cx: "10",
    cy: "10",
    r: "9",
    stroke: "black",
    strokeWidth: "2"
  }));
};

var Container$1 = /*#__PURE__*/styled.div.withConfig({
  displayName: "Tooltipstyles__Container",
  componentId: "sc-j9m9kt-0"
})(["", ";background-color:", ";"], {
  "padding": "0.5rem"
}, function (_ref) {
  var theme = _ref.theme;
  return theme.colors.title;
});
var Price = /*#__PURE__*/styled.p.withConfig({
  displayName: "Tooltipstyles__Price",
  componentId: "sc-j9m9kt-1"
})(["color:", ";font-family:", ";font-weight:", ";font-size:", ";letter-spacing:1px;"], function (_ref2) {
  var theme = _ref2.theme;
  return theme.colors.background;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.fontFamily["default"];
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.fontWeight.bold;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.fontSize.large;
});
var Date$1 = /*#__PURE__*/styled.p.withConfig({
  displayName: "Tooltipstyles__Date",
  componentId: "sc-j9m9kt-2"
})(["color:", ";font-family:", ";font-weight:", ";font-size:0.625rem;"], function (_ref6) {
  var theme = _ref6.theme;
  return theme.colors.placeholder;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.fontFamily["default"];
}, function (_ref8) {
  var theme = _ref8.theme;
  return theme.fontWeight.regular;
});

var Tooltip = function Tooltip(_ref) {
  var active = _ref.active,
      payload = _ref.payload,
      label = _ref.label;

  if (!active) {
    return null;
  }

  var price = Number(payload == null ? void 0 : payload[0].value);
  var formattedDate = formatDateInTimeZone(label);
  var formattedPrice = formatPrice(price, 5);
  return React__default.createElement(Container$1, null, React__default.createElement(Price, null, formattedPrice), React__default.createElement(Date$1, null, formattedDate));
};

var VISIBLE_TICKERS = 7;
var formatYAxis = function formatYAxis(value) {
  return "$" + Math.round(Number(value));
};
var formatXAxis = function formatXAxis(value, index, type) {
  var _axisInfo$type;

  var formattedDate = format(new Date(Number(value)), 'PPp').split(',').join(''); // retrieved data will have the following format:  Mar 21 2022 1:01 PM

  var dateParts = formattedDate.split(' ');
  var _dateParts$ = dateParts[0],
      month = _dateParts$ === void 0 ? '' : _dateParts$,
      _dateParts$2 = dateParts[1],
      day = _dateParts$2 === void 0 ? '' : _dateParts$2,
      _dateParts$3 = dateParts[2],
      year = _dateParts$3 === void 0 ? '' : _dateParts$3,
      _dateParts$4 = dateParts[3],
      hour = _dateParts$4 === void 0 ? '' : _dateParts$4,
      _dateParts$5 = dateParts[4],
      suffix = _dateParts$5 === void 0 ? '' : _dateParts$5;
  var axisInfo = {
    day: day,
    month: month,
    hour: hour + " " + suffix,
    month_before_days: index === 0 ? month : day,
    "default": month === 'Jan' ? year : month
  };
  return (_axisInfo$type = axisInfo[type]) != null ? _axisInfo$type : value;
};
var getBodyStyles = function getBodyStyles(themeColor, themeFontSize) {
  var styles = {
    xAxisTick: {
      fill: themeColor,
      fontSize: themeFontSize,
      textAnchor: 'start',
      transform: 'translate(0, 5)'
    },
    yAxisTick: {
      fill: themeColor,
      fontSize: themeFontSize,
      transform: 'translate(0, 0)'
    },
    lineChartMargin: {
      top: 30,
      left: 20,
      right: 30,
      bottom: 20
    },
    xAxisPadding: {
      left: 0,
      right: 20
    }
  };
  return styles;
}; // ticksArray needs to be set manually, tickersCount wont't work for strings & allowDuplicatedCategory={false} won't work for custom tickers

var getTicksFromData = function getTicksFromData(data) {
  var dataLength = data.length;
  var increaseBy = Math.round(dataLength / VISIBLE_TICKERS);
  var ticksArray = [];

  for (var index = 0; index < dataLength; index += increaseBy) {
    ticksArray.push(data[index].name);
  }

  return ticksArray;
};

var renderCustomTooltip = function renderCustomTooltip(props) {
  return React__default.createElement(Tooltip, _extends$4({}, props));
};

var ChartBody = function ChartBody(_ref) {
  var data = _ref.data,
      loading = _ref.loading,
      _ref$xAxisFormat = _ref.xAxisFormat,
      xAxisFormat = _ref$xAxisFormat === void 0 ? 'hour' : _ref$xAxisFormat;
  var containerRef = useRef(null);

  var _useContext = useContext(ThemeContext),
      colors = _useContext.colors,
      fontSize = _useContext.fontSize;

  var styles = getBodyStyles(colors.label, fontSize.small);
  var hasDefaultFormatType = xAxisFormat === 'default';
  var ticksArray = getTicksFromData(data);

  if (loading) {
    return React__default.createElement(ChartLoading, null);
  }

  return React__default.createElement("div", {
    ref: containerRef
  }, React__default.createElement(ResponsiveContainer, {
    width: "100%",
    minHeight: 240
  }, React__default.createElement(LineChart, {
    data: data,
    margin: styles.lineChartMargin
  }, React__default.createElement(CartesianGrid, {
    stroke: colors.meu,
    vertical: false
  }), React__default.createElement(XAxis, {
    allowDuplicatedCategory: hasDefaultFormatType,
    interval: "preserveStart",
    dataKey: "name",
    ticks: ticksArray,
    tickLine: false,
    axisLine: false,
    tick: styles.xAxisTick,
    tickFormatter: function tickFormatter(value, index) {
      return formatXAxis(value, index, xAxisFormat);
    },
    padding: styles.xAxisPadding
  }), React__default.createElement(YAxis, {
    tickLine: false,
    axisLine: false,
    tick: styles.yAxisTick,
    tickMargin: 10,
    tickCount: 6,
    allowDecimals: false,
    domain: ['dataMin', 'dataMax'],
    tickFormatter: formatYAxis
  }), React__default.createElement(Tooltip$1, {
    content: renderCustomTooltip,
    isAnimationActive: false,
    cursor: false,
    offset: 15,
    allowEscapeViewBox: {
      x: true,
      y: true
    }
  }), React__default.createElement(Line, {
    type: "monotone",
    dataKey: "value",
    stroke: "#F7931A",
    strokeWidth: 2,
    dot: false,
    activeDot: React__default.createElement(CustomDot, null)
  }))));
};

var useSetTabs = function useSetTabs(_ref) {
  var isLaptopWide = _ref.isLaptopWide,
      moreStatHref = _ref.moreStatHref,
      isAsset = _ref.isAsset,
      intervals = _ref.intervals,
      hasOpenMoreStats = _ref.hasOpenMoreStats;

  var _useState = useState(),
      tabsItems = _useState[0],
      setTabsItems = _useState[1];

  useEffect(function () {
    setTabsItems(intervals.map(function (item) {
      return {
        name: item
      };
    }));
  }, [intervals]);
  useEffect(function () {
    if (!tabsItems) return;
    var isLastItemOpenMoreStats = tabsItems[tabsItems.length - 1].name === 'Open more stats';

    if (isLastItemOpenMoreStats) {
      return setTabsItems(tabsItems.filter(function (item) {
        return item.name !== 'Open more stats';
      }));
    }

    if (isLaptopWide && !isAsset) {
      if (!isLastItemOpenMoreStats && hasOpenMoreStats) {
        setTabsItems([].concat(tabsItems, [{
          name: 'Open more stats',
          href: moreStatHref,
          icon: React__default.createElement(SvgArrowRight, null)
        }]));
      }
    }
  }, [isAsset, isLaptopWide, intervals, moreStatHref, hasOpenMoreStats]);
  return tabsItems;
};

var TabTag = function TabTag(tab) {
  return React__default.createElement(tab.tag, {}, React__default.createElement(React__default.Fragment, null, tab.name, " ", tab.icon));
};

var TabLink = function TabLink(props) {
  var AnchorComponent = props.anchorComponent,
      href = props.href,
      target = props.target,
      name = props.name,
      icon = props.icon;
  var isValidAnchorComponent = isValidElement(AnchorComponent);

  if (isValidAnchorComponent) {
    return React__default.createElement(AnchorComponent, _extends$4({}, props));
  }

  return React__default.createElement("a", {
    target: target,
    rel: "noopener",
    href: href
  }, name, " ", icon);
};

var _templateObject$2;
var Li = /*#__PURE__*/styled.li(_templateObject$2 || (_templateObject$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border-bottom: ", ";\n  border-right: ", ";\n  min-width: auto;\n  width: ", ";\n  background-color: ", ";\n  text-align: center;\n  font-size: ", ";\n  font-weight: ", ";\n  font-family: ", ";\n  line-height: 1.3125rem;\n  letter-spacing: 1px;\n  transition: 0.3s all ease-in-out;\n  white-space: nowrap;\n  & > * {\n    padding: ", ";\n    text-decoration: none;\n    width: 100%;\n    font-weight: ", ";\n    color: ", ";\n    cursor: pointer;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    svg {\n      margin-left: 2px;\n      transform: scale(0.7);\n      & > * {\n        stroke: ", ";\n      }\n    }\n  }\n  &:hover {\n    background: ", ";\n    * {\n      color: ", ";\n      svg {\n        * {\n          fill: ", ";\n          stroke: ", ";\n        }\n      }\n    }\n  }\n  a {\n    letter-spacing: 0;\n  }\n  &:first-child {\n    border-left: ", ";\n  }\n  &:last-child {\n    border-right: ", ";\n  }\n  @media ", " {\n    font-size: ", ";\n    a {\n      font-size: ", ";\n      padding: ", ";\n    }\n  }\n  @media ", " {\n    font-size: ", ";\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return "2px solid " + theme.colors.title;
}, function (_ref2) {
  var theme = _ref2.theme;
  return "2px solid " + theme.colors.title;
}, function (_ref3) {
  var variant = _ref3.variant;
  return variant === 'chart' && '100%';
}, function (_ref4) {
  var theme = _ref4.theme,
      active = _ref4.active;
  return active ? theme.colors.title : theme.colors.background;
}, function (_ref5) {
  var theme = _ref5.theme;
  return theme.typography.paragraph.fontSize.large.mobile;
}, function (_ref6) {
  var theme = _ref6.theme;
  return theme.typography.paragraph.fontWeight.xLarge;
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.typography.paragraph.fontFamily.quote;
}, function (_ref8) {
  var variant = _ref8.variant;
  return variant === 'chart' ? '0.5rem 0' : '0.5rem 1rem';
}, function (_ref9) {
  var theme = _ref9.theme;
  return theme.fontWeight.bold;
}, function (_ref10) {
  var theme = _ref10.theme,
      active = _ref10.active;
  return active ? theme.colors.background : theme.colors.title;
}, function (_ref11) {
  var active = _ref11.active,
      theme = _ref11.theme;
  return active && theme.colors.title;
}, function (_ref12) {
  var theme = _ref12.theme;
  return theme.colors.title;
}, function (_ref13) {
  var theme = _ref13.theme;
  return theme.colors.background;
}, function (_ref14) {
  var theme = _ref14.theme;
  return theme.colors.background;
}, function (_ref15) {
  var theme = _ref15.theme;
  return theme.colors.background;
}, function (_ref16) {
  var theme = _ref16.theme,
      variant = _ref16.variant;
  return variant === 'chart' ? 'none' : "2px solid " + theme.colors.title;
}, function (_ref17) {
  var variant = _ref17.variant;
  return variant === 'chart' && 'none';
}, MEDIA_QUERIES.laptop, function (_ref18) {
  var variant = _ref18.variant,
      theme = _ref18.theme;
  return variant === 'chart' && theme.typography.paragraph.fontSize.large.pc;
}, function (_ref19) {
  var variant = _ref19.variant,
      theme = _ref19.theme;
  return variant === 'chart' && theme.typography.paragraph.fontSize.large.mobile;
}, function (_ref20) {
  var variant = _ref20.variant;
  return variant === 'chart' ? '0.5rem 0' : '0.5rem 1.5rem';
}, MEDIA_QUERIES.desktop, function (_ref21) {
  var variant = _ref21.variant,
      theme = _ref21.theme;
  return variant === 'chart' && theme.typography.paragraph.fontSize.large.mobile;
});

var Tab = function Tab(_ref) {
  var tab = _ref.tab,
      variant = _ref.variant,
      _ref$active = _ref.active,
      active = _ref$active === void 0 ? false : _ref$active,
      _ref$shallowNavigatio = _ref.shallowNavigation,
      shallowNavigation = _ref$shallowNavigatio === void 0 ? true : _ref$shallowNavigatio,
      onActiveTabChange = _ref.onActiveTabChange,
      AnchorComponent = _ref.anchorComponent;

  var handleTabClick = function handleTabClick() {
    tab.onClick == null ? void 0 : tab.onClick();
    onActiveTabChange == null ? void 0 : onActiveTabChange(tab.name);
  };

  var renderTab = function renderTab() {
    var target = tab.target ? tab.target : '_self';

    if (tab.tag) {
      return React__default.createElement(TabTag, _extends$4({}, tab, {
        tag: tab.tag,
        target: target
      }));
    }

    return React__default.createElement(TabLink, _extends$4({}, tab, {
      target: target,
      shallowNavigation: shallowNavigation,
      anchorComponent: AnchorComponent
    }));
  };

  return React__default.createElement(Li, {
    active: active,
    key: tab.name,
    variant: variant,
    onClick: handleTabClick
  }, renderTab());
};

var _templateObject$1;
var Ul = /*#__PURE__*/styled.ul(_templateObject$1 || (_templateObject$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  border-top: ", ";\n  display: flex;\n  overflow-x: auto;\n  -ms-overflow-style: none; /* for Internet Explorer, Edge */\n  scrollbar-width: none; /* for Firefox */\n  &::-webkit-scrollbar {\n    display: none;\n  }\n"])), function (_ref) {
  var theme = _ref.theme;
  return "2px solid " + theme.colors.title;
});

var Tabs = function Tabs(_ref) {
  var items = _ref.items,
      variant = _ref.variant,
      activeTab = _ref.activeTab,
      onActiveTabChange = _ref.onActiveTabChange,
      AnchorComponent = _ref.anchorComponent;

  var renderTab = function renderTab(tab) {
    var isTabActive = activeTab === tab.name;
    return React__default.createElement(Tab, {
      tab: tab,
      variant: variant,
      active: isTabActive,
      anchorComponent: AnchorComponent,
      onActiveTabChange: onActiveTabChange
    });
  };

  return React__default.createElement(Ul, null, items.map(renderTab));
};

var ChartTabs = function ChartTabs(_ref) {
  var items = _ref.items,
      activeTab = _ref.activeTab,
      _ref$displayAsButtons = _ref.displayAsButtons,
      displayAsButtons = _ref$displayAsButtons === void 0 ? false : _ref$displayAsButtons,
      onTabChange = _ref.onTabChange;

  if (!items) {
    return null;
  }

  var tabItemTag = function tabItemTag(item) {
    return displayAsButtons || item != null && item.href ? 'button' : '';
  };

  var newTabItems = items == null ? void 0 : items.map(function (item) {
    return _extends$4({}, item, {
      tag: tabItemTag(item)
    });
  });
  return React__default.createElement(Tabs, {
    items: newTabItems,
    variant: "chart",
    activeTab: activeTab,
    onActiveTabChange: onTabChange
  });
};

var Chart = function Chart(_ref) {
  var name = _ref.name,
      shortname = _ref.shortname,
      logoSrc = _ref.logoSrc,
      price = _ref.price,
      intervals = _ref.intervals,
      data = _ref.data,
      _ref$info = _ref.info,
      info = _ref$info === void 0 ? [{
    title: '',
    value: ''
  }] : _ref$info,
      variant = _ref.variant,
      moreStatHref = _ref.moreStatHref,
      loading = _ref.loading,
      captionLink = _ref.captionLink,
      captionDate = _ref.captionDate,
      xAxisFormat = _ref.xAxisFormat,
      onTabChange = _ref.onTabChange;
  var isLaptopWide = useMediaQuery(MEDIA_QUERIES.laptop);
  var isAsset = variant === 'asset';
  var hasOpenMoreStats = Boolean(moreStatHref);
  var tabsItems = useSetTabs({
    isLaptopWide: isLaptopWide,
    moreStatHref: moreStatHref,
    isAsset: isAsset,
    intervals: intervals,
    hasOpenMoreStats: hasOpenMoreStats
  });

  var _useTabs = useTabs(intervals[0]),
      activeTab = _useTabs[0],
      setActiveTab = _useTabs[1];

  var isLaptopAndAssetVariant = isLaptopWide && isAsset;

  var onActiveTabChange = function onActiveTabChange(tab) {
    setActiveTab(tab);
    onTabChange == null ? void 0 : onTabChange(tab);
  };

  return React__default.createElement("div", null, React__default.createElement(ChartWrapper, {
    variant: variant
  }, React__default.createElement(ChartHeader, {
    name: name,
    shortname: shortname,
    price: price,
    change: data.change,
    logoSrc: logoSrc,
    option: activeTab
  }), React__default.createElement(AssetInfo, {
    info: info,
    variant: variant,
    visible: isAsset
  }), !tabsItems || isLaptopAndAssetVariant ? null : React__default.createElement(ChartTabs, {
    items: tabsItems,
    onTabChange: onActiveTabChange,
    activeTab: activeTab
  }), React__default.createElement(ChartBodyContainer, null, React__default.createElement(ChartBody, {
    data: data.data,
    loading: loading,
    xAxisFormat: xAxisFormat
  })), isLaptopAndAssetVariant ? React__default.createElement(ChartTabs, {
    items: tabsItems,
    displayAsButtons: true,
    onTabChange: onActiveTabChange,
    activeTab: activeTab
  }) : React__default.createElement(ChartFooter, {
    href: moreStatHref,
    displaySection: hasOpenMoreStats
  })), React__default.createElement(ChartCaption, {
    link: captionLink,
    date: captionDate
  }));
};

var _path$1, _path2$1;

function _extends$1() { _extends$1 = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var SvgCons = function SvgCons(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    width: 21,
    height: 21,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    d: "M19.97 10.675a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
    fill: "#F8E2E2"
  })), _path2$1 || (_path2$1 = /*#__PURE__*/React.createElement("path", {
    d: "M14.86 8.2a1 1 0 0 0-1.414-1.414L14.86 8.2Zm-7.778 4.95a1 1 0 1 0 1.414 1.414L7.082 13.15Zm6.364 1.414a1 1 0 0 0 1.414-1.414l-1.414 1.414Zm-4.95-7.778A1 1 0 1 0 7.082 8.2l1.414-1.414Zm10.475 3.889a8 8 0 0 1-8 8v2c5.523 0 10-4.477 10-10h-2Zm-8 8a8 8 0 0 1-8-8h-2c0 5.523 4.477 10 10 10v-2Zm-8-8a8 8 0 0 1 8-8v-2c-5.523 0-10 4.477-10 10h2Zm8-8a8 8 0 0 1 8 8h2c0-5.523-4.477-10-10-10v2Zm2.475 4.11-3.182 3.183 1.414 1.414L14.86 8.2l-1.414-1.414Zm-3.182 3.183L7.082 13.15l1.414 1.414 3.182-3.182-1.414-1.414Zm4.596 3.182-3.182-3.182-1.414 1.414 3.182 3.182 1.414-1.414Zm-3.182-3.182L8.496 6.786 7.082 8.2l3.182 3.182 1.414-1.414Z",
    fill: "#DE726E"
  })));
};

var _path, _path2;

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var SvgPros = function SvgPros(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    width: 20,
    height: 20,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    d: "M1 10.208c0-4.805 4.03-8.7 9-8.7s9 3.895 9 8.7-4.03 8.7-9 8.7-9-3.895-9-8.7Z",
    fill: "#DCEFD6"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    d: "m13.142 8.258-4.267 4.125-1.455-1.406M10 1.507c-4.97 0-9 3.896-9 8.7 0 4.806 4.03 8.7 9 8.7s9-3.894 9-8.7c0-4.804-4.03-8.7-9-8.7Z",
    stroke: "#52AF33",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9;
var Container = /*#__PURE__*/styled.div(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  border: 1px solid #cfcfcf;\n  border-radius: 19px;\n  padding: 10px 15px;\n  .button-container {\n    grid-column: 1 / -1;\n    margin: 20px 0;\n    a {\n      width: 100%;\n    }\n    @media ", " {\n      grid-column: span 1;\n      margin: 0;\n    }\n  }\n"])), MEDIA_QUERIES.tablet);
var Header = /*#__PURE__*/styled.div(_templateObject2 || (_templateObject2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: grid;\n  grid-template-columns: 1fr 1fr 1fr;\n  margin-bottom: 15px;\n  @media ", " {\n    grid-template-columns: ", ";\n  }\n"])), MEDIA_QUERIES.mobileL, function (_ref) {
  var columns = _ref.columns;
  return columns === 'two' ? '1fr 1fr' : '2fr 1fr 1fr';
});
var HeaderContainer = /*#__PURE__*/styled.div(_templateObject3 || (_templateObject3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  &:first-child {\n    grid-column: 1 / span 2;\n  }\n  h2 {\n    margin: 10px 0;\n  }\n  img {\n    width: 40px;\n    @media ", " {\n      width: 50px;\n    }\n  }\n  @media ", " {\n    justify-content: ", ";\n    &:first-child {\n      grid-column: 1;\n    }\n  }\n"])), MEDIA_QUERIES.mobileL, MEDIA_QUERIES.mobileL, function (_ref2) {
  var align = _ref2.align;
  return align === 'left' ? 'flex-start' : align === 'center' ? 'center' : 'flex-end';
});
var Content = /*#__PURE__*/styled.div(_templateObject4 || (_templateObject4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-family: ", ";\n  font-weight: ", ";\n"])), function (_ref3) {
  var theme = _ref3.theme;
  return theme.typography.paragraph.fontFamily.serif;
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.typography.paragraph.fontWeight.regular;
});
var Button = /*#__PURE__*/styled.a(_templateObject5 || (_templateObject5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  background: #5fb486;\n  padding: 15px;\n  min-width: 150px;\n  border-radius: 10px;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  font-weight: 600;\n  color: white;\n"])));
var StyledP = /*#__PURE__*/styled.p(_templateObject6 || (_templateObject6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  font-size: 18px;\n  margin-top: 5px;\n"])));
var ProsAndCons = /*#__PURE__*/styled.div(_templateObject7 || (_templateObject7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  margin: 15px 0;\n  padding: 10px 0;\n  border-top: 1px solid #cfcfcf;\n  border-bottom: 1px solid #cfcfcf;\n"])));
var PacContainer = /*#__PURE__*/styled.div(_templateObject8 || (_templateObject8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: grid;\n  grid-template-columns: 1fr;\n  grid-gap: 20px;\n  margin-top: 10px;\n  @media ", " {\n    grid-template-columns: 1fr 1fr;\n  }\n"])), MEDIA_QUERIES.tablet);
var PacItem = /*#__PURE__*/styled.div(_templateObject9 || (_templateObject9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n  display: flex;\n  font-size: 18px;\n  margin-bottom: 10px;\n  &:last-child {\n    margin: 0;\n  }\n  svg {\n    flex-shrink: 0;\n    margin-right: 5px;\n    margin-top: 3px;\n  }\n"])));

var Overview = function Overview(_ref) {
  var heading = _ref.heading,
      url = _ref.url,
      logoUrl = _ref.logoUrl,
      buttonLabel = _ref.buttonLabel,
      about = _ref.about,
      pros = _ref.pros,
      cons = _ref.cons,
      details = _ref.details,
      follow = _ref.follow;
  return React__default.createElement(Container, null, React__default.createElement(Header, {
    columns: buttonLabel ? 'three' : 'two'
  }, React__default.createElement(HeaderContainer, {
    align: "left"
  }, React__default.createElement(Heading, null, React__default.createElement(Link$2, {
    href: url,
    variant: "external",
    rel: follow ? 'follow' : 'nofollow'
  }, heading))), React__default.createElement(HeaderContainer, {
    align: buttonLabel ? 'center' : 'right'
  }, React__default.createElement("img", {
    src: logoUrl,
    alt: "logo"
  })), buttonLabel && React__default.createElement(HeaderContainer, {
    align: "right",
    className: "button-container"
  }, React__default.createElement(Button, {
    href: url,
    rel: follow ? 'follow' : 'nofollow'
  }, buttonLabel))), React__default.createElement(Content, null, React__default.createElement("div", null, React__default.createElement(H4, null, "About"), React__default.createElement(StyledP, null, about)), pros && cons && React__default.createElement(ProsAndCons, null, React__default.createElement(H4, null, "Pros & Cons"), React__default.createElement(PacContainer, null, React__default.createElement("div", null, pros.map(function (item) {
    return React__default.createElement(PacItem, null, React__default.createElement(SvgPros, null), React__default.createElement("span", null, item));
  })), React__default.createElement("div", null, cons.map(function (item) {
    return React__default.createElement(PacItem, null, React__default.createElement(SvgCons, null), React__default.createElement("span", null, item));
  })))), React__default.createElement("div", null, React__default.createElement(H4, null, "Details"), React__default.createElement(StyledP, null, details))));
};

export { A, Arrow, Blockquote, Button$1 as Button, Chart, CoinpaperThemeProvider, Figcaption, Figure, H0, H1, H2, H3, H4, Heading, Heading3, Heading4, Heading5, Heading6, Label, Link$2 as Link, LinkWrapper, MEDIA_QUERIES, Overview, P, Table, TableBase, TableCell, TableContainer, TableHeader, TableRow, Tabs, Tbody, Td, Th, Tweet, TweetLoading, convertFromBaseToRem, formatDateInTimeZone, formatPrice, getButtonStyle, getParagraphStyle, getTheme, h1Style, h3Style, theme, useMediaQuery, useScript, useTabs, useTweet };
//# sourceMappingURL=coinpaper-components.esm.js.map
